"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.GatewayApiClient = exports.getDefaultClient = exports.disableDefaultClient = exports.setDefaultClient = void 0;
const jsonwebtoken_1 = require("jsonwebtoken");
const logger_1 = require("./logger");
const request_1 = require("./request");
const status_1 = require("./status");
let explicitlySetDefaultClient = false;
let defaultClient = null;
function setDefaultClient(client) {
    explicitlySetDefaultClient = client !== null;
    defaultClient = client;
}
exports.setDefaultClient = setDefaultClient;
function disableDefaultClient() {
    explicitlySetDefaultClient = true;
    defaultClient = null;
}
exports.disableDefaultClient = disableDefaultClient;
function getDefaultClient() {
    return defaultClient;
}
exports.getDefaultClient = getDefaultClient;
class GatewayApiClient {
    get token() {
        return this.privateToken;
    }
    set token(value) {
        if (value && !/^Bearer |^Basic /.test(value)) {
            throw new Error("Incomplete token. Token must have 'Bearer ' or 'Basic '");
        }
        this.privateToken = value;
        this.bearerOpts.headers.Authorization = this.privateToken;
    }
    get clientId() {
        return this.internalClientId;
    }
    set clientId(value) {
        this.internalClientId = value;
        this.basicOpts.auth.username = this.internalClientId;
    }
    get clientSecret() {
        return this.internalClientSecret;
    }
    set clientSecret(value) {
        this.internalClientSecret = value;
        this.basicOpts.auth.password = this.internalClientSecret;
    }
    get statusChecksEnabled() {
        return this.internalStatusChecksEnabled;
    }
    get hasBearer() {
        return !!this.privateToken;
    }
    get fallbackOpts() {
        return this.hasBearer ? this.bearerOpts : this.basicOpts;
    }
    constructor(params) {
        this.m2mAuthorizationHeader = null;
        this.m2mRefreshAtMs = null;
        this.gettingM2MOpts = null;
        const { baseURL, privateToken, clientId, clientSecret, request, logger, profileNetworkRequests, status } = params;
        this.cacheURL = params.cacheURL;
        this.baseURL = params.baseURL;
        this.baseServiceURL = params.baseServiceURL;
        this.auth0ClientId = params.auth0ClientId;
        this.auth0ClientSecret = params.auth0ClientSecret;
        let nodeProcess = params.nodeProcess;
        if (nodeProcess === undefined) {
            nodeProcess = process;
        }
        if ((0, logger_1.isSillyLogger)(logger)) {
            this.logger = logger;
        }
        else if ((0, logger_1.isNestLogger)(logger)) {
            this.logger = (0, logger_1.wrapNestLogger)(logger);
        }
        this.privateToken = privateToken;
        if (!explicitlySetDefaultClient) {
            defaultClient = this;
        }
        this.req = request || new request_1.Request(undefined, this.logger, profileNetworkRequests);
        if (this.isNode(nodeProcess)) {
            this.req.setUserAgentHeader(process.env.DD_SERVICE || process.env.DATADOG_APP_NAME || process.env.PROJECT_NAME || clientId);
        }
        const baseOpts = {
            baseURL,
        };
        this.noAuthOpts = Object.assign({}, baseOpts);
        this.bearerOpts = Object.assign(Object.assign({}, baseOpts), {
            headers: {
                Authorization: null,
            },
        });
        this.basicOpts = Object.assign(Object.assign({}, baseOpts), {
            auth: {
                password: clientSecret,
                username: clientId,
            },
        });
        this.token = privateToken;
        this.clientId = clientId;
        this.clientSecret = clientSecret;
        this.internalStatusChecksEnabled = (status === null || status === void 0 ? void 0 : status.enableStatusChecks) || false;
        this.statusService = new status_1.StatusService(status);
    }
    getRequestConfig(options, serviceName) {
        var _a;
        if (!this.token || (options === null || options === void 0 ? void 0 : options.useBasicAuth)) {
            (_a = this.logger) === null || _a === void 0 ? void 0 : _a.silly('useBasicAuth is deprecated. Please use { useM2MAuth: true } instead.');
        }
        return Object.assign(Object.assign({}, (this.token && !(options === null || options === void 0 ? void 0 : options.useBasicAuth) ? this.bearerOpts : this.basicOpts)), this.getBaseRequestConfig(options, serviceName));
    }
    getBaseRequestConfig(options, serviceName) {
        var _a;
        const returnValue = {
            cancelToken: (_a = options === null || options === void 0 ? void 0 : options.cancelSource) === null || _a === void 0 ? void 0 : _a.token,
            baseURL: this.baseURL,
        };
        if (options === null || options === void 0 ? void 0 : options.useGatewayCache) {
            if (!this.cacheURL) {
                throw new Error('cacheURL must be provided in GatewayApiClient constructor before useGatewayCache can be used!');
            }
            returnValue.baseURL = this.cacheURL;
        }
        if (serviceName) {
            returnValue.baseURL = this.getServiceURL(serviceName);
        }
        return returnValue;
    }
    getServiceURL(serviceName) {
        if (!this.baseServiceURL) {
            throw new Error('baseServiceURL must be provided in GatewayApiClient constructor before useServiceGateway can be used! The gateway-module you are using requires this new parameter.');
        }
        const parts = this.baseServiceURL.toLowerCase().split('//');
        if (parts[0] !== 'http:' && parts[0] !== 'https:') {
            throw new Error('baseServiceURL must begin with either http or https, not "' + parts[0] + '"');
        }
        const startsWithIP = /^(?:\d{1,3}\.){3}\d{1,3}/;
        if (!parts[1].startsWith('localhost') && !startsWithIP.test(parts[1])) {
            parts[1] = `${serviceName}.${parts[1]}`;
        }
        return parts.join('//');
    }
    isDown(name) {
        return this.statusService.isDown(name);
    }
    isNode(process) {
        var _a;
        return typeof ((_a = process === null || process === void 0 ? void 0 : process.versions) === null || _a === void 0 ? void 0 : _a.node) !== 'undefined';
    }
    async getM2MRequestConfig(options, serviceName) {
        if (!this.auth0ClientId || !this.auth0ClientSecret) {
            throw new Error('auth0ClientId and auth0ClientSecret must be provided in GatewayApiClient constructor before useM2MAuth can be used!');
        }
        if (!this.baseServiceURL) {
            throw new Error('baseServiceURL must be provided in GatewayApiClient constructor before useM2MAuth can be used!');
        }
        if (this.needNewM2MToken()) {
            await this.fetchNewM2MToken();
        }
        const returnValue = this.getBaseRequestConfig(options, serviceName);
        returnValue.headers = { Authorization: this.m2mAuthorizationHeader };
        return returnValue;
    }
    needNewM2MToken() {
        var _a;
        if (!this.m2mAuthorizationHeader) {
            return true;
        }
        if (!this.m2mRefreshAtMs || this.m2mRefreshAtMs < Date.now()) {
            (_a = this.logger) === null || _a === void 0 ? void 0 : _a.silly('M2M token for gateway-core is going to expire in the next hour, triggering refresh of token.');
            return true;
        }
        return false;
    }
    async fetchNewM2MToken() {
        var _a, _b, _c;
        try {
            if (!this.gettingM2MOpts) {
                (_a = this.logger) === null || _a === void 0 ? void 0 : _a.silly('A new M2M token is needed for gateway-core. Getting a new one from Auth0.');
                const baseURL = this.getServiceURL('auth-service').replace(/\/$/, '');
                this.gettingM2MOpts = this.req.send('/v1/oauth/token', {
                    method: 'post',
                    baseURL,
                    data: {
                        client_id: this.auth0ClientId,
                        client_secret: this.auth0ClientSecret,
                        grant_type: 'client_credentials',
                    },
                });
            }
            else {
                (_b = this.logger) === null || _b === void 0 ? void 0 : _b.silly('Joining the queue of calls waiting for a new M2M token from Auth0.');
            }
            const response = await this.gettingM2MOpts;
            this.gettingM2MOpts = null;
            const { access_token: token } = response.data;
            const tokenStr = token.replace(/^Bearer +/, '');
            const decodedJwt = (0, jsonwebtoken_1.decode)(tokenStr);
            const expiresAtSeconds = decodedJwt.exp;
            const oneHourInSeconds = 3600;
            const millisecondsInOneSecond = 1000;
            this.m2mAuthorizationHeader = `Bearer ${token}`;
            this.m2mRefreshAtMs = (expiresAtSeconds - oneHourInSeconds) * millisecondsInOneSecond;
        }
        catch (err) {
            (_c = this.logger) === null || _c === void 0 ? void 0 : _c.silly('Error getting M2M token', err);
            throw new Error(`Error getting M2M token for client ${this.auth0ClientId}`);
        }
    }
}
exports.GatewayApiClient = GatewayApiClient;
//# sourceMappingURL=client.js.map